import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

/**
 * localStorage key for the explicit theme preference. Absent = follow the
 * system. Kept in sync with the pre-paint script in index.html.
 */
const STORAGE_KEY = "portal-theme";

/** Fired on <html> whenever the applied theme changes. */
export const THEME_CHANGE_EVENT = "portal-themechange";

function systemTheme(): Theme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function appliedTheme(): Theme {
  return document.documentElement.classList.contains("dark")
    ? "dark"
    : "light";
}

function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle("dark", theme === "dark");
  document.documentElement.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT));
}

/**
 * Theme state + toggle. Defaults to the system color scheme; an explicit
 * toggle stores a preference. Toggling back to the system's current value
 * clears the stored preference so the portal resumes following the system.
 */
export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(appliedTheme);

  const toggle = useCallback(() => {
    const next: Theme = appliedTheme() === "dark" ? "light" : "dark";
    try {
      if (next === systemTheme()) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, next);
      }
    } catch {
      // Storage unavailable — theme still applies for this session.
    }
    applyTheme(next);
    setTheme(next);
  }, []);

  // Follow system changes while no explicit preference is stored.
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      let stored: string | null = null;
      try {
        stored = localStorage.getItem(STORAGE_KEY);
      } catch {
        /* treat as no preference */
      }
      if (stored === "light" || stored === "dark") return;
      const next = systemTheme();
      applyTheme(next);
      setTheme(next);
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return { theme, toggle };
}
